import type { ApolloError } from '@apollo/client';
import { EventPageType } from '@graphql/generated-base';
import { useGetFeatureSetQuery } from '@graphql/queries/featureSet.graphql.generated';
import { useCallback, useState, useMemo } from 'react';
import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { isEventPageVisible } from '@shared/utils/pageVisibility';
import { isUserGeneratedMigratedSquatter, toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import type { FeatureSet } from './featureSet.types';
import { FeatureSetType } from './featureSet.types';

/**
 * useFeatureSet retrieves which FeatureSet areas (Website, Registry, Rsvp) are enabled for the current event.
 *
 * Returns:
 * - isInitialized: true once the query has resolved with an event.
 * - features: FeatureSet map, or undefined while loading or when the event is missing.
 * - hasFeatureSetEnable: helper to check a single feature's enabled state.
 * - hasErrors: the last ApolloError, if any.
 * - refetchFeatureSet: re-runs the underlying query.
 */

export const useFeatureSet = () => {
  const eventNameMatch = useEventNameMatch();
  const [hasErrors, setHasErrors] = useState<Maybe<ApolloError>>(null);

  const { loading, refetch, data } = useGetFeatureSetQuery({
    batchMode: 'off',
    ssr: true,
    variables: {
      eventName: eventNameMatch.eventName || '',
    },
    onError(err) {
      setHasErrors(err);
    },
  });

  const featureSetOpts: FeatureSet | undefined = useMemo(() => {
    if (loading) {
      return undefined;
    }

    // Bail out if data is missing (e.g., query failed or returned no data)
    // This prevents undefined values from being treated as "feature enabled"
    if (!data?.eventByName) {
      return undefined;
    }

    const eventByName = data.eventByName;
    // A missing welcome PageInfo (null) must count as website-disabled, not enabled — otherwise a
    // feature-only event with no welcome row keeps Website enabled (so useGuestSiteRedirection never
    // redirects) while useDefaultPage has no default page to render, stranding the guest on a blank
    // root route.
    // A missing PageInfo (null) means the feature is not present — treat it as disabled, not enabled.
    // Otherwise an event with absent welcome + registry + rsvp rows would report { website: false,
    // registry: true } and useGuestSiteRedirection would send guests to a /registry that isn't there.
    const nativeWelcomePageEnabled = !!eventByName.welcomePage && !eventByName.welcomePage.disabled;
    const registryPageEnabled = !!eventByName.registryPage && !eventByName.registryPage.disabled;
    const rsvpPageEnabled = !!eventByName.rsvpPage && !eventByName.rsvpPage.disabled;

    // Website is enabled when the native Welcome page is on, OR when any genuine website custom
    // page is visible. Without the custom-page fallback, events whose only website content is a
    // non-Welcome custom page get redirected to /find even though they have content.
    //
    // The migrated registry page is type=custom but is its own feature (with a dedicated /registry
    // landing), not website content. Counting it here would enable the Website feature for an event
    // whose welcome page is disabled and whose only custom page is the migrated registry — stranding
    // guests on the hidden welcome page instead of redirecting them to /registry. A user-generated
    // page that merely squats the 'registrylinks' slug is real website content, so it still counts.
    const hasVisibleCustomPage = eventByName.pages.some(page => {
      if (page.type !== EventPageType.custom || !isEventPageVisible(page.visibility)) {
        return false;
      }
      const isMigratedRegistry = toCanonicalMigratedSlug(page.pageSlug) === 'registrylinks' && !isUserGeneratedMigratedSquatter(page);
      return !isMigratedRegistry;
    });
    const websiteFeatureEnabled = nativeWelcomePageEnabled || hasVisibleCustomPage;

    return {
      [FeatureSetType.Website]: websiteFeatureEnabled,
      [FeatureSetType.Registry]: registryPageEnabled,
      [FeatureSetType.Rsvp]: rsvpPageEnabled,
    };
  }, [data, loading]);

  const hasFeatureSetEnable = useCallback(
    (feature: FeatureSetType) => {
      return featureSetOpts ? !!featureSetOpts[feature] : false;
    },
    [featureSetOpts],
  );

  return {
    isInitialized: !loading && !!data?.eventByName,
    features: featureSetOpts,
    hasFeatureSetEnable,
    hasErrors,
    refetchFeatureSet: refetch,
  };
};
